import { useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from '@/hooks/useToast'
import { 
  UserPlusIcon, 
  PencilIcon, 
  TrashIcon,
  ShieldCheckIcon,
  UserIcon 
} from '@heroicons/react/24/outline'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { ToastContainer } from '@/components/common/ToastContainer'

interface User {
  id: string
  username: string
  email?: string
  name: string
  role: 'admin' | 'user'
  status: 'active' | 'inactive'
  createdAt: string
  lastLogin?: string
}

export function UserManagement() {
  const { user: currentUser } = useAuthStore()
  const { toasts, showToast, hideToast } = useToast()
  const [users, setUsers] = useState<User[]>([
    {
      id: '1',
      username: 'admin',
      email: 'admin@demo.com',
      name: 'Admin User',
      role: 'admin',
      status: 'active',
      createdAt: '2024-01-01T00:00:00Z',
      lastLogin: '2024-01-20T10:30:00Z',
    },
    {
      id: '2',
      username: 'johndoe',
      email: 'john@demo.com',
      name: 'John Doe',
      role: 'user',
      status: 'active',
      createdAt: '2024-01-02T00:00:00Z',
      lastLogin: '2024-01-19T15:45:00Z',
    },
    {
      id: '3',
      username: 'janesmith',
      email: 'jane@demo.com',
      name: 'Jane Smith',
      role: 'user',
      status: 'inactive',
      createdAt: '2024-01-03T00:00:00Z',
    },
  ])
  const [showAddModal, setShowAddModal] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  })

  if (currentUser?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <ShieldCheckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You need administrator privileges to access user management.
          </p>
        </div>
      </div>
    )
  }

  const handleDeleteUser = (userId: string) => {
    if (userId === currentUser.id) {
      showToast('error', 'Cannot Delete Account', 'You cannot delete your own account')
      return
    }
    
    const userToDelete = users.find(user => user.id === userId)
    if (!userToDelete) return
    
    setConfirmationModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete "${userToDelete.name}"? This action cannot be undone.`,
      onConfirm: () => {
        setUsers(users.filter(user => user.id !== userId))
        showToast('success', 'User Deleted', `${userToDelete.name} has been successfully deleted`)
      }
    })
  }

  const toggleUserStatus = (userId: string) => {
    setUsers(users.map(user => 
      user.id === userId 
        ? { ...user, status: user.status === 'active' ? 'inactive' : 'active' } 
        : user
    ))
  }

  const handleCloseConfirmation = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }))
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage user accounts and permissions
          </p>
        </div>
        <button type="button"
          onClick={() => { setShowAddModal(true) }}
          className="btn-primary flex items-center space-x-2"
        >
          <UserPlusIcon className="w-5 h-5" />
          <span>Add User</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-head">User</th>
                <th className="table-head">Username</th>
                <th className="table-head">Role</th>
                <th className="table-head">Status</th>
                <th className="table-head">Created</th>
                <th className="table-head">Last Login</th>
                <th className="table-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="table-row">
                  <td className="table-cell">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.name}
                        </div>
                        {user.id === currentUser.id && (
                          <div className="text-xs text-primary-600">You</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">{user.username}</td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'admin'
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>
                      {user.role === 'admin' ? 'Administrator' : 'User'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <button type="button"
                      onClick={() => { toggleUserStatus(user.id) }}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    >
                      {user.status === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="table-cell">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    {user.lastLogin 
                      ? new Date(user.lastLogin).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <button type="button"
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit user"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button type="button"
                        onClick={() => { handleDeleteUser(user.id) }}
                        disabled={user.id === currentUser.id}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete user"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Add New User
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  User creation functionality will be implemented in a future update.
                </p>
                <div className="flex justify-end space-x-3">
                  <button type="button"
                    onClick={() => { setShowAddModal(false) }}
                    className="btn-ghost"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCloseConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />

      <ToastContainer toasts={toasts} onHideToast={hideToast} />
    </div>
  )
}
